/**
 * "Did the turn I lost sight of actually happen?" — the turn machine's recovery question.
 *
 * *A dropped connection must not cost a turn* (`src/quest-v2-api/README.md` §3). The server
 * finishes and commits a turn whose client went away, which means every way of losing the
 * stream — a backgrounded mobile tab the OS killed, a proxy that cut the connection, a 409
 * from another tab — has the same correct answer, and it is **ask the server**, never
 * "assume it failed" and never "submit it again".
 *
 * The good version of this is `GET /stories/{id}/turns/current`, which re-attaches to the
 * running turn and replays its buffered events through the same renderer. That is deferred
 * (`src/quest-v2-api/README.md` §11, root `TODO.md`). Until then `GET /stories/{id}` carries
 * `active_turn` and `chapter_count`, and those two fields answer the question completely.
 */
import type { QueryClient } from '@tanstack/react-query';
import { queryKeys, storyQueryOptions } from '@/api/queries';

export type ReconcileResult =
  /** The chapter is in the database. The story queries now hold it; clear the turn store. */
  | 'landed'
  /** A turn is still resolving on the server. Keep showing it, keep polling, do not act. */
  | 'in_flight'
  /** No turn running and no new chapter: it really did not happen. The player may retry. */
  | 'lost'
  /** The question could not be asked — offline, 401 mid-recovery. Try again later. */
  | 'unknown';

/** How often to re-ask while a turn is known to be resolving elsewhere. */
export const ACTIVE_TURN_POLL_MS = 5_000;

/**
 * @param expectedChapter the chapter number the lost turn was writing (`turn.started`, or a
 * 409's `active_turn.chapter_number`). Null when even that is unknown — then only
 * `active_turn` can be trusted, because `chapter_count` alone cannot say whose chapter it is.
 */
export async function reconcileTurn(
  client: QueryClient,
  storyId: string,
  expectedChapter: number | null,
): Promise<ReconcileResult> {
  let story;
  try {
    // `staleTime: 0` because the whole point is to bypass the cached answer that is
    // currently wrong.
    story = await client.fetchQuery({ ...storyQueryOptions(storyId), staleTime: 0 });
  } catch {
    return 'unknown';
  }

  if (expectedChapter !== null && story.chapter_count >= expectedChapter) {
    // The chapter exists. The feed has to see it, and the character panel with it — this is
    // the one path where the client did *not* receive the snapshot the commit usually writes.
    await Promise.all([
      client.invalidateQueries({ queryKey: queryKeys.chapters(storyId) }),
      client.invalidateQueries({ queryKey: queryKeys.storyState(storyId) }),
      client.invalidateQueries({ queryKey: queryKeys.stories() }),
    ]);
    return 'landed';
  }

  return story.active_turn ? 'in_flight' : 'lost';
}
